import { logger } from 'common/services/logger';

export type ForcedLogoutCapture = () => void;

const captures = new Set<ForcedLogoutCapture>();
let inProgress = false;
let clearInProgress: ReturnType<typeof setTimeout> | undefined;

/**
 * How long a forced logout counts as in progress. Long enough for the page's `beforeunload` to
 * fire as it leaves for Keycloak's logout. If it does not leave, because someone answered the
 * browser's "Leave site?" prompt with Stay, a logout they choose later is not mistaken for a forced
 * one, and still removes every kept copy.
 */
const IN_PROGRESS_FOR_MS = 1_000;

// A throwing capture must never stop the logout, nor starve the other captures.
const safeInvoke = (capture: ForcedLogoutCapture) => {
  try {
    capture();
  } catch (error) {
    logger.warn('[forcedLogout] capture threw', { error });
  }
};

/**
 * Lets a page keep what it needs before a forced logout leaves it. keycloak-js leaves with a
 * synchronous `window.location.replace`, so a capture must finish synchronously. One refused token
 * refresh can start a forced logout more than once on the same page, so a capture runs every time.
 */
const registerCapture = (capture: ForcedLogoutCapture): (() => void) => {
  captures.add(capture);

  return () => {
    captures.delete(capture);
  };
};

/** Marks the logout as forced, then runs every capture. Called by `logoutToCurrentPage` only. */
const begin = () => {
  inProgress = true;
  clearTimeout(clearInProgress);
  clearInProgress = setTimeout(() => {
    inProgress = false;
  }, IN_PROGRESS_FOR_MS);
  captures.forEach(safeInvoke);
};

/** Whether the page is leaving for a forced logout; see `IN_PROGRESS_FOR_MS`. */
const isInProgress = () => inProgress;

const _resetForTests = () => {
  captures.clear();
  clearTimeout(clearInProgress);
  inProgress = false;
};

export const forcedLogout = { registerCapture, begin, isInProgress, _resetForTests };
